'use client';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useMediaQuery } from '@/app/_hooks/use-media-query';
import animationData from './animation.json';

// Lazy load Lottie to avoid loading ~300KB on every page
const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

interface HeroAdvancedAdditionalContentProps {
  theme?: string;
  isDark?: boolean;
}

// Color definitions [R, G, B, A] in 0-1 range
const STROKE_COLORS = {
  theme0: [0, 0, 0, 1], // Black
  theme1: [1, 1, 1, 1], // White
} as const;

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return --
   Recoloring pokes an opaque Lottie animation JSON at runtime. lottie-web types
   `animationData` as `any`, so there is no real type to lean on and the deep
   stroke path can't be modelled cleanly. The proper fix — recolor via CSS
   instead of mutating the JSON — is a deliberate later rebuild (see the
   hero-advanced Lottie recolor TODO). Until then these unsafe-* warnings are
   knowingly waived, scoped to just this file. */
export function HeroAdvancedAdditionalContent({
  theme,
  isDark,
}: HeroAdvancedAdditionalContentProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Clone animation data and replace stroke color based on theme or isDark
  const themedAnimationData = useMemo(() => {
    const useWhite = isDark || theme === 'theme1';
    const strokeColor = useWhite ? STROKE_COLORS.theme1 : STROKE_COLORS.theme0;

    const data = JSON.parse(JSON.stringify(animationData));
    // Navigate to stroke color: layers[0].shapes[0].it[7].c.k
    // (7 is the stroke element "Stroke 1" in the shape group)
    try {
      data.layers[0].shapes[0].it[7].c.k = strokeColor;
    } catch {
      // If path doesn't exist, return original data
      return animationData;
    }
    return data;
  }, [theme, isDark]);

  return (
    <div
      // eslint-disable-next-line no-restricted-syntax -- fixed px kept intentionally; this component is slated for a full CSS rebuild (see Lottie recolor TODO), so a custom design token isn't worth it here
      className="z-50 h-[220px] w-full max-w-[350px] 2xl:mb-24"
      data-module="hero-advanced-additional-content"
      aria-hidden="true"
    >
      <Lottie
        animationData={themedAnimationData}
        loop={!prefersReducedMotion}
        autoplay={!prefersReducedMotion}
        className="h-auto w-full"
      />
    </div>
  );
}
